const localApiBaseUrl =
  typeof window !== "undefined" && isLocalWebHost(window.location.hostname)
    ? "http://localhost:8787"
    : "";

export const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || localApiBaseUrl;
const tokenKey = "mediapilotx:api-token";

function isLocalWebHost(hostname: string) {
  return hostname === "localhost"
    || hostname === "127.0.0.1"
    || hostname === "0.0.0.0"
    || /^10\.\d+\.\d+\.\d+$/.test(hostname)
    || /^192\.168\.\d+\.\d+$/.test(hostname)
    || /^172\.(1[6-9]|2\d|3[01])\.\d+\.\d+$/.test(hostname);
}

export type ApiHealth = {
  status: string;
  at: string;
};

export type DuplicateMatch = {
  id: string;
  title: string;
  platformId?: string;
  score: number;
  createdAt?: string;
};

export type DuplicateCheckResult = {
  matches: DuplicateMatch[];
  status: "clear" | "similar" | "duplicate";
};

export type CollectedPreviewItem = {
  title: string;
  url: string;
  author?: string;
  publishedAt?: string;
  content: string;
  summary?: string;
};

export type RemoteKnowledgeNode = {
  id: string;
  name: string;
  title: string;
  path: string;
  kind: "folder" | "file";
  children: RemoteKnowledgeNode[];
  tags?: string[];
  size?: number;
  updatedAt?: string;
  nodeId?: string;
  desktopNodeId?: string;
  industry?: string;
  publishStatus?: string;
  publishedAt?: string;
  publishNote?: string;
};

export type KnowledgePublishRecord = {
  id: string;
  knowledgeNodeId: string;
  platformName: string;
  siteName: string;
  accountName: string;
  publishUrl: string;
  publishStatus: string;
  publishedAt: string;
  publishNote: string;
  createdAt: string;
  updatedAt: string;
};

export type RemoteKnowledgeArticle = {
  key: string;
  title: string;
  tags: string[];
  nodeId?: string;
  desktopNodeId?: string;
  industry?: string;
  publishStatus?: string;
  publishedAt?: string;
  publishNote?: string;
  publishRecords?: KnowledgePublishRecord[];
  updatedAt: string;
  size: number;
  content: string;
};

export async function fetchHealth(): Promise<ApiHealth | null> {
  if (!apiBaseUrl) return null;
  const response = await fetch(`${apiBaseUrl}/health`);
  if (!response.ok) throw new Error("API health check failed");
  return response.json() as Promise<ApiHealth>;
}

export async function apiLogin(email: string, password = "") {
  if (!apiBaseUrl) return null;
  const response = await fetch(`${apiBaseUrl}/auth/login`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email, password })
  });
  if (!response.ok) throw new Error("API login failed");
  const result = await response.json();
  if (result?.token) localStorage.setItem(tokenKey, result.token);
  return result;
}

export async function fetchBootstrap<T>(): Promise<T | null> {
  if (!apiBaseUrl) return null;
  return apiRequest<T>("/bootstrap");
}

export async function createRemote<T>(route: string, payload: Record<string, unknown>): Promise<T | null> {
  if (!apiBaseUrl) return null;
  return apiRequest<T>(`/${route}`, {
    method: "POST",
    body: JSON.stringify(payload)
  });
}

export async function updateRemote<T>(route: string, id: string, payload: Record<string, unknown>): Promise<T | null> {
  if (!apiBaseUrl) return null;
  return apiRequest<T>(`/${route}/${id}`, {
    method: "PUT",
    body: JSON.stringify(payload)
  });
}

export async function deleteRemote<T>(route: string, id: string): Promise<T | null> {
  if (!apiBaseUrl) return null;
  return apiRequest<T>(`/${route}/${id}`, {
    method: "DELETE"
  });
}

export async function postRemote<T>(path: string, payload: Record<string, unknown> = {}): Promise<T | null> {
  if (!apiBaseUrl) return null;
  return apiRequest<T>(path, {
    method: "POST",
    body: JSON.stringify(payload)
  });
}

export async function previewCollectionSource(payload: {
  url: string;
  sourceType: string;
  limit?: number;
}): Promise<{ items: CollectedPreviewItem[]; fetchedAt: string } | null> {
  if (!apiBaseUrl) return null;
  return apiRequest<{ items: CollectedPreviewItem[]; fetchedAt: string }>("/collect/preview", {
    method: "POST",
    body: JSON.stringify(payload)
  });
}

export async function checkDuplicates(payload: {
  kind: "asset" | "text";
  contentHash?: string;
  text?: string;
  platformId?: string;
  accountId?: string;
  excludeId?: string;
}): Promise<DuplicateCheckResult | null> {
  if (!apiBaseUrl) return null;
  return apiRequest<DuplicateCheckResult>("/duplicates/check", {
    method: "POST",
    body: JSON.stringify(payload)
  });
}

export async function uploadRemoteAsset<T>(payload: {
  id: string;
  file: File;
  name: string;
  assetType: string;
  businessLineId: string;
  platformId?: string;
  tags: string;
  contentHash?: string;
}): Promise<T | null> {
  if (!apiBaseUrl) return null;
  const form = new FormData();
  form.set("id", payload.id);
  form.set("file", payload.file);
  form.set("name", payload.name || payload.file.name);
  form.set("asset_type", payload.assetType);
  form.set("business_line_id", payload.businessLineId);
  form.set("platform_id", payload.platformId || "");
  form.set("tags", payload.tags);
  if (payload.contentHash) form.set("content_hash", payload.contentHash);
  const token = localStorage.getItem(tokenKey);
  const response = await fetch(`${apiBaseUrl}/assets/upload`, {
    method: "POST",
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {})
    },
    body: form
  });
  if (!response.ok) {
    const text = await response.text();
    throw new Error(text || `Asset upload failed: ${response.status}`);
  }
  return response.json() as Promise<T>;
}

export async function uploadGeneratedImage(payload: {
  file: Blob;
  storageKey: string;
  projectId: string;
  variantIndex: number;
  pageIndex: number;
  source: string;
}): Promise<{ storageKey: string; path: string; size: number; contentType: string } | null> {
  if (!apiBaseUrl) return null;
  const form = new FormData();
  form.set("file", payload.file, `variant-${payload.variantIndex}-${payload.pageIndex}.jpg`);
  form.set("storage_key", payload.storageKey);
  form.set("project_id", payload.projectId);
  form.set("variant_index", String(payload.variantIndex));
  form.set("page_index", String(payload.pageIndex));
  form.set("source", payload.source);
  const token = localStorage.getItem(tokenKey);
  const response = await fetch(`${apiBaseUrl}/assets/generated-image`, {
    method: "POST",
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {})
    },
    body: form
  });
  if (!response.ok) {
    const text = await response.text();
    throw new Error(text || `Generated image upload failed: ${response.status}`);
  }
  return response.json() as Promise<{ storageKey: string; path: string; size: number; contentType: string }>;
}

export async function fetchKnowledgeTree(): Promise<{ tree: RemoteKnowledgeNode[] } | null> {
  if (!apiBaseUrl) return null;
  return apiRequest<{ tree: RemoteKnowledgeNode[] }>("/knowledge/tree?compact=1", {}, 15000);
}

export async function fetchKnowledgeFile(path: string): Promise<RemoteKnowledgeArticle | null> {
  if (!apiBaseUrl) return null;
  return apiRequest<RemoteKnowledgeArticle>(`/knowledge/file?path=${encodeURIComponent(path)}`);
}

export async function updateKnowledgePublishStatus(payload: {
  nodeId: string;
  publishStatus: string;
  publishNote?: string;
  publishedAt?: string;
}): Promise<{ id: string; publishStatus: string; publishedAt: string; publishNote: string } | null> {
  if (!apiBaseUrl) return null;
  return apiRequest(`/knowledge/nodes/${payload.nodeId}/publish-status`, {
    method: "PATCH",
    body: JSON.stringify({
      publishStatus: payload.publishStatus,
      publishNote: payload.publishNote || "",
      publishedAt: payload.publishedAt || "",
    })
  });
}

export async function createKnowledgePublishRecord(
  nodeId: string,
  payload: Record<string, unknown>,
): Promise<{ record: KnowledgePublishRecord } | null> {
  if (!apiBaseUrl) return null;
  return apiRequest(`/knowledge/nodes/${nodeId}/publish-records`, {
    method: "POST",
    body: JSON.stringify(payload)
  });
}

export async function updateKnowledgePublishRecord(
  recordId: string,
  payload: Record<string, unknown>,
): Promise<{ record: KnowledgePublishRecord } | null> {
  if (!apiBaseUrl) return null;
  return apiRequest(`/knowledge/publish-records/${recordId}`, {
    method: "PUT",
    body: JSON.stringify(payload)
  });
}

export async function deleteKnowledgePublishRecord(recordId: string): Promise<{ deleted: boolean } | null> {
  if (!apiBaseUrl) return null;
  return apiRequest(`/knowledge/publish-records/${recordId}`, {
    method: "DELETE"
  });
}

export async function uploadKnowledgeMarkdown(payload: {
  file: File;
  path: string;
  title?: string;
  tags?: string;
}): Promise<{ key: string; path: string; title: string; tags: string[]; size: number } | null> {
  if (!apiBaseUrl) return null;
  const form = new FormData();
  form.set("file", payload.file);
  form.set("path", payload.path);
  form.set("title", payload.title || payload.file.name.replace(/\.md$/i, ""));
  form.set("tags", payload.tags || "");
  const token = localStorage.getItem(tokenKey);
  const response = await fetch(`${apiBaseUrl}/knowledge/upload`, {
    method: "POST",
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {})
    },
    body: form
  });
  if (!response.ok) {
    const text = await response.text();
    throw new Error(text || `Knowledge upload failed: ${response.status}`);
  }
  return response.json() as Promise<{ key: string; path: string; title: string; tags: string[]; size: number }>;
}

async function apiRequest<T>(
  path: string,
  options: RequestInit = {},
  timeoutMs = 30000,
): Promise<T> {
  const token = localStorage.getItem(tokenKey);
  const controller = new AbortController();
  const timeout = window.setTimeout(() => controller.abort(), timeoutMs);
  let response: Response;
  try {
    response = await fetch(`${apiBaseUrl}${path}`, {
      ...options,
      signal: options.signal || controller.signal,
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...options.headers
      }
    });
  } catch (error) {
    if (error instanceof DOMException && error.name === "AbortError") {
      throw new Error("请求超时，请稍后重试");
    }
    throw new Error("网络请求失败，请检查网络或刷新页面重试");
  } finally {
    window.clearTimeout(timeout);
  }
  if (!response.ok) {
    const text = await response.text();
    let message = text;
    try {
      const parsed = JSON.parse(text) as { message?: string; error?: string; hint?: string };
      message = parsed.message || parsed.error || parsed.hint || text;
    } catch {
      message = text;
    }
    throw new Error(message || `API request failed: ${response.status}`);
  }
  return response.json() as Promise<T>;
}
